import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { type ProductsData, type ProductsPayload } from '@/app/api/search/products/createProductsData.ts';
import { SEARCH_TYPE, type SearchInfoData, type SearchInfoDetailData } from '@/app/search/types.ts';
import { productsDataDefault } from '@/app/components/search/common/reloadData/productsDataDefault.ts';

import { type GoToRoute } from '@/app/components/search/pages/indexClient.tsx';
import { createQueryString } from '@/utils/queryUtils.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import {
  fireSearchDestinationsEventsAfter,
  fireSearchDestinationsEventsBefore,
} from '@/app/common/PageWrapperClientEventsUtils.tsx';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import { setSearchLoadingStage, watchSearchDataReady } from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import { PATTERN_LIMIT } from '../../Utilities';
import type { Localization } from '../Localization';
import getSearchParams from '../getSearchParams';
import type { CuriosityParams } from '../../Curiosity';
import { type DefaultDestItems, type HandleReloadDealsParams, ReloadType } from '../../utils';
import { hasAllMandatoryParams } from '../urlValidator';
import clientAPICall from '../clientAPICall.ts';
import { APIStatusEnum } from '../reloadData/APIStatusEnum.ts';
import { API_NAME } from '../apiTypes.ts';

enum LEISURE_TYPE {
  BEACH = 'L1',
  EXOTIC = 'L2',
  SKI = 'L3',
  CITY = 'L4',
  SPA = 'L5',
}

const createQueryParamsForReload = (searchParams: SearchParams): SearchParams => {
  const queryStringWithoutOffset = Object.entries(searchParams).reduce(
    (result, [key, val]) => (key === 'offset' ? result : { ...result, [key]: val }),
    {}
  );
  const searchParamsFresh = getSearchParams();
  return {
    ...searchParamsFresh,
    ...queryStringWithoutOffset,
  };
};

type ResetProducts = {
  setIsLoading: (isLoading: boolean) => void;
  showMoreToggle?: (show: boolean) => void;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  curiosityParams: CuriosityParams;
  localization: Localization;
  defaultDestItems: DefaultDestItems[];
  goToRoute: GoToRoute;
  starTrack: StarTrack;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  commonData: CommonData;
  handleReloadDealsParams: HandleReloadDealsParams;
  handleAfterHaveResponse: (productsDataAfterUpdate: ProductsData) => void;
};

export const resetProducts = async ({
  setIsLoading,
  showMoreToggle,
  setOffset,
  curiosityParams,
  localization,
  defaultDestItems,
  goToRoute,
  starTrack,
  searchInfoData,
  searchInfoDetailData,
  commonData,
  handleReloadDealsParams,
  handleAfterHaveResponse,
}: ResetProducts) => {
  const { searchType } = searchInfoData;
  const { searchParams, reloadType } = handleReloadDealsParams;
  const isTag = searchParams?.[URLParameters.DESTINATION]?.toString().indexOf('TAG') !== -1;

  // TODO: Do we need this check? If new searches always refresh the page then this should not be needed
  if (reloadType === ReloadType.SEARCH && searchType === SEARCH_TYPE.PRODUCTS) {
    goToRoute(createQueryParamsForReload(searchParams));
  } else {
    const searchParamsFresh = getSearchParams();
    const hasQueryString =
      searchParamsFresh && Object.keys(searchParamsFresh).length !== 0 && searchParamsFresh.constructor === Object;
    if (
      reloadType === ReloadType.SEARCH ||
      reloadType === ReloadType.FILTER ||
      (hasQueryString && hasAllMandatoryParams(searchParamsFresh))
    ) {
      delete searchParams[URLParameters.OFFSET];
      window.history.replaceState(null, '', `?${createQueryString(searchParams)}`);
    }

    /* TO CHECK */
    const isBeach =
      searchParams[URLParameters.DESTINATION] && searchParams[URLParameters.DESTINATION].includes(LEISURE_TYPE.BEACH);
    if (hasQueryString && !isBeach && hasAllMandatoryParams(searchParamsFresh)) {
      delete searchParams[URLParameters.FAUNA];
      delete searchParams[URLParameters.BEACH_TYPE];
      delete searchParams[URLParameters.BLUE_FLAG];
      delete searchParams[URLParameters.CLIMATE];
      delete searchParams[URLParameters.ENTERTAINMENTS];
      delete searchParams[URLParameters.WATERSPORT];
      delete searchParams[URLParameters.WATER_WAVES];
    }

    window.history.replaceState(null, '', `?${createQueryString(searchParams)}`);

    // Arms the watchdog without going through handleSearchChangedBase, so name the stage here.
    setSearchLoadingStage('destinationsReload');
    setIsLoading(true);

    const productsPayload: ProductsPayload = {
      searchParams,
      isSearchFromServer: false,
      commonData: { ...commonData, pageStartTime: Date.now() },
      localization,
      curiosityParams,
    };

    const productsData = await clientAPICall<ProductsPayload, ProductsData>({
      apiName: API_NAME.PRODUCTS,
      payload: productsPayload,
      traceParent: searchInfoData.searchTraceParent,
      commonData,
      defaultResponse: {
        ...productsDataDefault,
        apiStatus: APIStatusEnum.ERROR,
      },
    });

    // The response is in, so nothing is waiting on the search any more. Before the event firing below,
    // because an uncaught throw from it skips both the cards and the flag that hides the placeholders.
    watchSearchDataReady();

    const resCityCards = productsData?.cityCards || [];

    if (reloadType === ReloadType.SEARCH) {
      fireSearchDestinationsEventsBefore({
        searchParams,
        localization,
        commonData,
        curiosityParams,
        searchInfoData,
        searchInfoDetailData,
        starTrack,
      });
      fireSearchDestinationsEventsAfter({
        searchParams,
        productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems,
        commonData,
        localization,
        curiosityParams,
        starTrack,
      });
    }

    if (showMoreToggle && !isTag) {
      showMoreToggle(resCityCards.length === PATTERN_LIMIT);
    }

    if (productsData) handleAfterHaveResponse(productsData);

    setOffset(0);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }
};
